/**
 * The lead brief: everything the backend already knows about an insight that
 * the portal was throwing away.
 *
 * A note on what is actually here, because it is not what the field names
 * suggest. `financialContext` is written by three different backend handlers
 * with three different shapes, and on the insights that fire in production
 * today (pre_approval_ready, high_value_prospect, calculator_engagement) every
 * one of its four keys is null - the mobile app never collects a credit score
 * or an annual income. Building a "financial snapshot" alone would have shown
 * an empty box on every real lead.
 *
 * What IS populated on every production insight is why the lead exists
 * (triggerData), how ready they are (qualificationData), what to do
 * (actionItems) and how to reach them (customerInfo). That is what this leads
 * with. Financial figures render when a handler does supply them - the refi
 * path writes balance, rate and savings - and the section disappears when it
 * has nothing to say, rather than showing a grid of dashes.
 */

import React from 'react';
import { AgentInsight } from '../types/agent';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import './LeadBrief.css';

interface LeadBriefProps {
	insight: AgentInsight;
}

type Row = { label: string; value: string };

const humanize = (key: string): string =>
	key
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^./, (c) => c.toUpperCase());

const titleCase = (value: string): string =>
	value.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

const isPresent = (value: unknown): boolean =>
	value !== null && value !== undefined && value !== '';

/**
 * Formats by meaning rather than by type, so a rate does not render as
 * currency and a score does not pick up a dollar sign.
 */
// Percentages are tested first, and deliberately so: `loanToValue` contains
// both "loan" and "value", so a currency-first check renders 69.3 as $69.30.
const PERCENT_KEY = /rate|percentage|ratio|loanToValue|ltv/i;
const CURRENCY_KEY = /balance|income|payment|savings|amount|equity|value|loan|principal/i;

const formatByKey = (key: string, value: unknown): string => {
	if (typeof value === 'number') {
		if (PERCENT_KEY.test(key)) {
			// Ratios arrive both as 0.36 and as 36 depending on the writer.
			const asPercent = /ratio/i.test(key) && value <= 1 ? value * 100 : value;
			return formatPercentage(asPercent, 2);
		}
		if (CURRENCY_KEY.test(key)) {
			return formatCurrency(value);
		}
		if (/score|count/i.test(key)) return String(Math.round(value));
		return String(value);
	}

	if (typeof value === 'string') return titleCase(value);
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';

	if (value && typeof value === 'object') {
		// propertyAddress and triggerData.context are flat maps.
		const parts = Object.entries(value as Record<string, unknown>)
			.filter(([, v]) => isPresent(v))
			.map(([k, v]) => (typeof v === 'number' ? `${humanize(k)} ${formatByKey(k, v)}` : String(v)));
		return parts.join(' · ');
	}

	return String(value);
};

/** Turns an arbitrary map into label/value rows, dropping anything empty. */
const toRows = (source: Record<string, unknown> | undefined, skip: string[] = []): Row[] => {
	if (!source) return [];
	return Object.entries(source)
		.filter(([key, value]) => !skip.includes(key) && isPresent(value))
		.filter(([, value]) => !(typeof value === 'object' && !Array.isArray(value) && Object.values(value as object).every((v) => !isPresent(v))))
		.map(([key, value]) => ({ label: humanize(key), value: formatByKey(key, value) }));
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
	<section className="lead-brief-section">
		<h4 className="lead-brief-heading">{title}</h4>
		{children}
	</section>
);

const RowGrid: React.FC<{ rows: Row[] }> = ({ rows }) => (
	<dl className="lead-brief-grid">
		{rows.map((row) => (
			<div key={row.label} className="lead-brief-row">
				<dt>{row.label}</dt>
				<dd>{row.value}</dd>
			</div>
		))}
	</dl>
);

export const LeadBrief: React.FC<LeadBriefProps> = ({ insight }) => {
	const financialRows = toRows(insight.financialContext as Record<string, unknown> | undefined);

	// `source` and `timestamp` are shown as prose above the grid, so they are
	// skipped here to avoid repeating them.
	const triggerContextRows = toRows(
		insight.triggerData?.context as Record<string, unknown> | undefined
	);
	const qualificationRows = toRows(insight.qualificationData);

	const actionItems = Array.isArray(insight.actionItems)
		? insight.actionItems.filter((item) => isPresent(item))
		: [];

	// Names are skipped: the dialog header already identifies the customer, and
	// a name is not a way to reach anybody.
	const contact = insight.customerInfo as Record<string, unknown> | undefined;
	const contactRows = toRows(contact, ['firstName', 'lastName', 'name', 'displayName']);

	const triggerSource = insight.triggerData?.source;
	const triggeredAt = insight.triggerData?.timestamp;

	const hasWhy = !!triggerSource || triggerContextRows.length > 0 || qualificationRows.length > 0;
	const hasAnything = hasWhy || actionItems.length > 0 || contactRows.length > 0 || financialRows.length > 0;

	if (!hasAnything) {
		return null;
	}

	return (
		<div className="lead-brief">
			{hasWhy && (
				<Section title="Why this lead">
					{triggerSource && (
						<p className="lead-brief-trigger">
							Flagged from {titleCase(triggerSource).toLowerCase()}
							{triggeredAt ? ` on ${new Date(triggeredAt).toLocaleDateString()}` : ''}.
						</p>
					)}
					{(triggerContextRows.length > 0 || qualificationRows.length > 0) && (
						<RowGrid rows={[...qualificationRows, ...triggerContextRows]} />
					)}
				</Section>
			)}

			{actionItems.length > 0 && (
				<Section title="Suggested next steps">
					<ul className="lead-brief-actions">
						{actionItems.map((item, index) => (
							<li key={index}>{item}</li>
						))}
					</ul>
				</Section>
			)}

			{contactRows.length > 0 && (
				<Section title="How to reach them">
					<RowGrid rows={contactRows} />
				</Section>
			)}

			{financialRows.length > 0 && (
				<Section title="Financial snapshot">
					<RowGrid rows={financialRows} />
				</Section>
			)}
		</div>
	);
};

export default LeadBrief;
